import { IEbiPortalTheme } from "dto/Ebi/IEbiOffice";

// Kolory portalu petenta (Ustawienia → Biuro Interesanta → Wygląd) jako zmienne CSS. Portal ustawia je na <html>
// (ebi/portal/useEbiPortalTheme), a podgląd w ustawieniach — na kontenerze makiety; w obu wypadkach działają te same
// style: arkusze portalu (styles/ebiPortal.scss) czytają --ebi-accent*, a wspólne pola i przełączniki — tokeny
// primary. Portal z projektu Claude Design używa już tylko akcentu — kolory tła (Background1/2) zostają w modelu, ale
// nie są wyświetlane.

/** Zmienne CSS motywu portalu (lista do sprzątania po wyjściu z portalu). */
export const EBI_PORTAL_THEME_VARIABLES = [
  "--ebi-accent",
  "--ebi-accent-hover",
  "--ebi-on-accent",
  "--ebi-accent-soft",
  "--ebi-accent-line",
  "--ebi-accent-tint",
  "--ebi-accent-panel",
  "--ebi-accent-panel-line",
  "--Nota-sys-light-primary",
  "--Nota-sys-light-on-primary",
  "--Nota-sys-light-primary-container",
  "--Nota-sys-light-on-primary-container",
] as const;

/** Akcent z projektu portalu (domyślny wariant urzędu) — dla niego odcienie pochodne to dokładnie kolory projektu. */
const DESIGN_ACCENT = "#014A93";
const DESIGN_ACCENT_SHADES = {
  "--ebi-accent-hover": "#003A75",
  "--ebi-accent-soft": "#DBEAF9",
  "--ebi-accent-line": "#9EB9DC",
  "--ebi-accent-tint": "#EEF3FB",
  "--ebi-accent-panel": "#F0F6FD",
  "--ebi-accent-panel-line": "#C9D3E6",
};

/** Akcent platformy — dla niego kolory pochodne to dokładnie tokeny Noty, nie ich przybliżenie. */
const PLATFORM_ACCENT = "#5D49C7";
const PLATFORM_PRIMARY_CONTAINER = "#E5DEFF";
const PLATFORM_ON_PRIMARY_CONTAINER = "#190064";

const WHITE = "#FFFFFF";
const BLACK = "#000000";
const DARK_TEXT = "#1C1B1F";

/** Próg jasności (WCAG), od którego ciemny napis kontrastuje lepiej niż biały — jak EbiMailTheme w BE. */
const LIGHT_THRESHOLD = 0.179;

const hexToRgb = (hex: string): [number, number, number] => {
  const value = hex.replace("#", "");
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
};

const rgbToHex = (rgb: number[]): string =>
  `#${rgb
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, "0")
    )
    .join("")
    .toUpperCase()}`;

/** Mieszanka dwóch kolorów: `weight` to udział pierwszego (0–1). */
const mix = (first: string, second: string, weight: number): string => {
  const a = hexToRgb(first);
  const b = hexToRgb(second);
  return rgbToHex(
    a.map((channel, i) => channel * weight + b[i] * (1 - weight))
  );
};

/** Względna luminancja WCAG 2.x koloru #RRGGBB (0 = czarny, 1 = biały). */
export const relativeLuminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex).map((channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Te same trzy kolory portalu (zapis #RRGGBB niezależnie od wielkości liter). */
export const samePortalTheme = (
  a?: IEbiPortalTheme | null,
  b?: IEbiPortalTheme | null
): boolean =>
  !!a &&
  !!b &&
  a.Background1.toUpperCase() === b.Background1.toUpperCase() &&
  a.Background2.toUpperCase() === b.Background2.toUpperCase() &&
  a.Accent.toUpperCase() === b.Accent.toUpperCase();

/**
 * Paleta primary motywu MUI dla portalu: `main` = akcent, `dark`/`light` = odcień najechania (przyciski MUI biorą na hover
 * `primary.dark`; platforma ma tam fiolet rozjaśniony o ~8% bieli — ten sam stosunek dla akcentu urzędu), `contrastText`
 * jak napis na akcencie.
 */
export const portalAccentPalette = (theme: IEbiPortalTheme) => {
  const accent = theme.Accent.toUpperCase();
  const hover = mix(WHITE, accent, 0.08);

  return {
    main: accent,
    light: hover,
    dark: hover,
    contrastText:
      relativeLuminance(accent) > LIGHT_THRESHOLD ? DARK_TEXT : WHITE,
  };
};

/**
 * Odcienie akcentu dla arkuszy portalu: najechanie (ciemniej), tła pasków i zaznaczeń (rozjaśnienia bielą). Dla akcentu
 * z projektu — dokładnie kolory projektu.
 */
const accentShades = (accent: string): Record<string, string> =>
  accent === DESIGN_ACCENT
    ? DESIGN_ACCENT_SHADES
    : {
        "--ebi-accent-hover": mix(accent, BLACK, 0.8),
        "--ebi-accent-soft": mix(accent, WHITE, 0.14),
        "--ebi-accent-line": mix(accent, WHITE, 0.38),
        "--ebi-accent-tint": mix(accent, WHITE, 0.07),
        "--ebi-accent-panel": mix(accent, WHITE, 0.05),
        "--ebi-accent-panel-line": mix(accent, WHITE, 0.22),
      };

/**
 * Zmienne CSS z akcentu urzędu: akcent i odcienie dla arkuszy portalu, napis na akcencie (biały albo ciemny wg jasności)
 * oraz tokeny primary dla wspólnych pól i przełączników (tło i tekst powiadomienia w odcieniu akcentu).
 */
export const portalThemeVariables = (
  theme: IEbiPortalTheme
): Record<string, string> => {
  const accent = theme.Accent.toUpperCase();
  const isPlatformAccent = accent === PLATFORM_ACCENT;
  const onAccent = relativeLuminance(accent) > LIGHT_THRESHOLD ? DARK_TEXT : WHITE;

  return {
    "--ebi-accent": accent,
    "--ebi-on-accent": onAccent,
    ...accentShades(accent),
    "--Nota-sys-light-primary": accent,
    "--Nota-sys-light-on-primary": onAccent,
    "--Nota-sys-light-primary-container": isPlatformAccent
      ? PLATFORM_PRIMARY_CONTAINER
      : mix(accent, WHITE, 0.16),
    "--Nota-sys-light-on-primary-container": isPlatformAccent
      ? PLATFORM_ON_PRIMARY_CONTAINER
      : mix(accent, BLACK, 0.4),
  };
};
